/**
 * Label-printer configuration (`devices.config_json` when `device_type = 'label_printer'`).
 *
 * A label printer is reached over the obrador's LAN by raw TCP (port 9100, the printer's own
 * "JetDirect" socket — a Zebra takes ZPL straight on it). The address is only meaningful
 * INSIDE that obrador's network (two obradors may both have a 192.168.1.80); the tenant is
 * kept apart by WHO sends the job — the tablet that is on that LAN — and by the RLS of
 * `devices`, never by the address itself.
 *
 * The label size is the size of the ROLL loaded in the printer, so a printer that prints
 * 50×30 mm reception labels and another that prints 100×60 mm ones each carry their own.
 * Resolution is NOT configurable on purpose: the ZPL renderer works at 203 dpi (8 dots/mm),
 * the Zebra GX420d's, and offering a dpi here would promise something it does not do.
 *
 * Unknown keys of `config_json` are kept as they are (HOOK: label-template binding, scale
 * parameters…): this schema only reads what a printer needs.
 */

import { z } from 'zod';

/** The raw-print TCP port every Zebra network print server listens on. */
export const LABEL_PRINTER_DEFAULT_PORT = 9100;

/** The smallest / largest roll width or height (mm) a desktop label printer takes. */
export const LABEL_PRINTER_MIN_SIZE_MM = 10;
export const LABEL_PRINTER_MAX_SIZE_MM = 200;

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const DIGITS_AND_DOTS = /^[0-9.]+$/;
const HOSTNAME =
  /^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i;

/**
 * Whether `value` is something a socket can be opened to: an IPv4 address or a host name.
 * It refuses what people paste by habit — a scheme (`http://…`), a path, a `:port` suffix,
 * spaces — and anything that LOOKS like an IP but is not one (`192.168.1`, `256.1.1.1`),
 * because the connect would fail later with a message the operator cannot act on.
 */
export function isValidPrinterHost(value: string): boolean {
  const host = value.trim();
  if (IPV4.test(host)) return true;
  if (DIGITS_AND_DOTS.test(host)) return false;
  return HOSTNAME.test(host);
}

export const LabelPrinterConfig = z
  .object({
    host: z.string().trim().refine(isValidPrinterHost, { message: 'invalid_printer_host' }),
    port: z.number().int().min(1).max(65535).default(LABEL_PRINTER_DEFAULT_PORT),
    label_width_mm: z
      .number()
      .min(LABEL_PRINTER_MIN_SIZE_MM)
      .max(LABEL_PRINTER_MAX_SIZE_MM)
      .optional(),
    label_height_mm: z
      .number()
      .min(LABEL_PRINTER_MIN_SIZE_MM)
      .max(LABEL_PRINTER_MAX_SIZE_MM)
      .optional(),
  })
  // A roll has a width AND a height: one without the other would print a label of a size
  // nobody chose (the other half silently falling back to the renderer's default).
  .refine((c) => (c.label_width_mm === undefined) === (c.label_height_mm === undefined), {
    message: 'label_size_incomplete',
    path: ['label_height_mm'],
  });
export type LabelPrinterConfig = z.infer<typeof LabelPrinterConfig>;

/**
 * The printer configuration stored in a device's `config_json`, or null when it has none
 * (a printer registered before it was given an address) or it does not validate. Never
 * throws: a bad row must read as "not configured", not take a screen down.
 */
export function parseLabelPrinterConfig(configJson: unknown): LabelPrinterConfig | null {
  const parsed = LabelPrinterConfig.safeParse(configJson);
  return parsed.success ? parsed.data : null;
}
